import type { ReactElement } from 'react';
import type { ValidationResult } from '@shared/types';

/**
 * Validation comes from the engines, so it is stated as fact: a slim banner of
 * errors, and a quieter line for warnings. No client-side judgement about
 * severity — the codes already carry it.
 *
 * MISSING_PRICE warnings are excluded here (V16 rate cards, phase 2b) — they
 * get their own calm, per-item note (MissingPriceNote, mounted alongside this
 * banner) rather than folding into this generic warnings line twice.
 */
export function ValidationBanner({
  validation,
}: {
  validation: ValidationResult;
}): ReactElement | null {
  const { errors } = validation;
  const warnings = validation.warnings.filter(
    (issue) => issue.code !== 'MISSING_PRICE',
  );
  if (errors.length === 0 && warnings.length === 0) return null;

  return (
    <div className="validation">
      {errors.length > 0 ? (
        <div className="validation-errors" role="status">
          <span className="validation-label">
            {errors.length === 1 ? '1 issue' : `${errors.length} issues`}
          </span>
          <ul>
            {errors.map((issue, index) => (
              <li key={`${issue.code}-${issue.productId ?? index}`}>{issue.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {warnings.length > 0 ? (
        <p className="validation-warnings">
          {warnings.map((issue) => issue.message).join(' ')}
        </p>
      ) : null}
    </div>
  );
}
